import { PdBaseUI } from "../base/pd-base-ui.js";
import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
//#region src/pd-form-fieldset/PdFormFieldset.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Groups form fields and returns their values as an object.
*
* PdFormFieldset wraps a native `<fieldset>` element and acts as a container
* for form fields. It aggregates all child field values into a single object
* and registers itself with the parent PdFormContainer as a single field.
*
* @tagname pd-form-fieldset
* @summary Groups form fields and returns their values as an object.
*
* @event pd-form-element-register - Fired when fieldset registers with parent container.
* @event pd-form-element-change - Fired when any child field changes. Detail contains aggregated object.
* @event pd-form-element-unregister - Fired when fieldset disconnects from parent container.
*
* @slot - Form fields to group (pd-input, pd-select, etc.).
*
* @cssprop --pd-fieldset-border - Border style. Default: `1px solid var(--pd-default-light-col)`.
* @cssprop --pd-fieldset-border-radius - Border radius. Default: `var(--pd-radius-md, 4px)`.
* @cssprop --pd-fieldset-bg-col - Background color. Default: `transparent`.
* @cssprop --pd-fieldset-padding - Inner padding. Default: `1rem`.
* @cssprop --pd-fieldset-gap - Gap between child fields. Default: `var(--pd-spacing-sm, 0.5rem)`.
* @cssprop --pd-fieldset-legend-col - Legend text color. Default: `var(--pd-default-dark-col)`.
* @cssprop --pd-fieldset-legend-font-size - Legend font size. Default: `1em`.
* @cssprop --pd-fieldset-legend-font-weight - Legend font weight. Default: `500`.
*
* @example
* ```html
* <pd-form-container>
*   <pd-form-row>
*     <pd-form-fieldset name="address" legend="Address" span="full">
*       <pd-form-row>
*         <pd-input valueName="street" label="Street" span="two-thirds"></pd-input>
*         <pd-input valueName="number" label="Nr." span="third"></pd-input>
*       </pd-form-row>
*       <pd-form-row>
*         <pd-input valueName="zip" label="ZIP" span="quarter"></pd-input>
*         <pd-input valueName="city" label="City" span="three-quarters"></pd-input>
*       </pd-form-row>
*     </pd-form-fieldset>
*   </pd-form-row>
* </pd-form-container>
* ```
*
* **Note:** Fieldsets must be wrapped in a pd-form-row for proper grid layout.
* Nesting pd-form-fieldset inside another pd-form-fieldset is not officially supported.
*/
var PdFormFieldset = class extends PdBaseUI {
	constructor(..._args) {
		super(..._args);
		this.name = "";
		this.legend = "";
		this.span = "full";
		this._fields = /* @__PURE__ */ new Map();
		this._onChildUnRegister = (e) => {
			const { name } = e.detail;
			console.debug("###### PdFormFieldset._onChildUnRegister", name);
			if (this._fields.has(name)) {
				this._fields.delete(name);
				this.requestUpdate();
				this._notifyContainer();
			}
			e.stopPropagation();
		};
		this._onChildUnRegisterBound = this._onChildUnRegister.bind(this);
	}
	static {
		this.styles = [PdBaseUI.styles, css`
      :host {
        display: block;
        width: 100%;
      }

      /* When used in pd-form-row, span only the 12 main columns, NOT the helper column */
      :host([span="full"]) {
        grid-column: span 12 !important;
      }

      fieldset {
        /* Customizable via CSS variables */
        border: var(
          --pd-fieldset-border,
          1px solid var(--pd-default-light-col)
        );
        border-radius: var(
          --pd-fieldset-border-radius,
          var(--pd-radius-md, 4px)
        );
        background-color: var(--pd-fieldset-bg-col, transparent);
        padding: var(--pd-fieldset-padding, 1rem);
        margin: 0;

        /* Layout for children */
        display: flex;
        flex-direction: column;
        gap: var(--pd-fieldset-gap, var(--pd-spacing-sm, 0.5rem));
      }

      legend {
        color: var(--pd-fieldset-legend-col, var(--pd-default-dark-col));
        font-family: var(--pd-default-font-content-family);
        font-size: var(--pd-fieldset-legend-font-size, 1em);
        font-weight: var(--pd-fieldset-legend-font-weight, 500);
        padding: 0 0.5em;
      }
    `];
	}
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener("pd-form-element-unregister", this._onChildUnRegisterBound);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener("pd-form-element-unregister", this._onChildUnRegisterBound);
		this._unRegisterAtContainer();
	}
	firstUpdated() {
		this._registerAtContainer();
	}
	render() {
		return html`
      <fieldset
        @pd-form-element-change="${this._onChildChange}"
        @pd-form-element-blur="${this._onChildBlur}"
        @pd-form-element-focus="${this._onChildFocus}"
        @pd-form-element-register="${this._onChildRegister}"
      >
        ${this.legend ? html`<legend>${this.legend}</legend>` : nothing}
        <slot></slot>
      </fieldset>
    `;
	}
	_onChildRegister(e) {
		const { name, value, parsedValue, state } = e.detail;
		console.debug("###### PdFormFieldset._onChildRegister", name, value, state);
		if (!this._fields.has(name)) {
			this._fields.set(name, {
				name,
				value,
				parsedValue,
				valid: state.valid,
				invalid: state.invalid,
				pristine: state.pristine,
				dirty: state.dirty,
				touched: state.touched,
				focused: state.focused,
				errors: {},
				element: e.target
			});
			this.requestUpdate();
			this._notifyContainer();
		}
		e.stopPropagation();
	}
	_onChildChange(e) {
		const { name, value, parsedValue, state } = e.detail;
		console.debug("###### PdFormFieldset._onChildChange", name, value, state);
		const field = this._fields.get(name);
		if (field) {
			field.value = value;
			field.parsedValue = parsedValue;
			field.pristine = state.pristine;
			field.dirty = state.dirty;
			field.touched = state.touched;
			field.focused = state.focused;
			field.valid = state.valid;
			field.invalid = state.invalid;
			this.requestUpdate();
			this._notifyContainer();
		}
		e.stopPropagation();
	}
	_onChildBlur(e) {
		const { name } = e.detail;
		const field = this._fields.get(name);
		if (field) {
			field.touched = true;
			field.focused = false;
		}
		e.stopPropagation();
	}
	_onChildFocus(e) {
		const { name } = e.detail;
		const field = this._fields.get(name);
		if (field) field.focused = true;
		e.stopPropagation();
	}
	/**
	* Checks if any child field is dirty.
	*/
	_isDirty() {
		for (const field of this._fields.values()) if (field.dirty) return true;
		return false;
	}
	/**
	* Checks if any child field is touched.
	*/
	_isTouched() {
		for (const field of this._fields.values()) if (field.touched) return true;
		return false;
	}
	/**
	* Checks if any child field is focused.
	*/
	_isFocused() {
		for (const field of this._fields.values()) if (field.focused) return true;
		return false;
	}
	/**
	* Checks if all child fields are pristine.
	*/
	_isPristine() {
		return !this._isDirty();
	}
	/**
	* Returns aggregated value object from all child fields.
	* @private
	*/
	_getAggregatedValue() {
		const obj = {};
		this._fields.forEach((field, key) => {
			obj[key] = field.value;
		});
		return obj;
	}
	/**
	* Returns aggregated parsed value object from all child fields.
	* @private
	*/
	_getAggregatedParsedValue() {
		const obj = {};
		this._fields.forEach((field, key) => {
			obj[key] = field.parsedValue;
		});
		return obj;
	}
	/**
	* Returns overall validity based on all child fields.
	* Fieldset is valid only if ALL children are valid.
	* @private
	*/
	_getOverallValidity() {
		if (this._fields.size === 0) return true;
		return ![...this._fields.values()].some((field) => field.invalid);
	}
	/**
	* Notifies parent container with aggregated value and state.
	* @private
	*/
	_notifyContainer() {
		const valid = this._getOverallValidity();
		this.dispatchEvent(new CustomEvent("pd-form-element-change", {
			detail: {
				name: this.name || this.id || "fieldset",
				value: this._getAggregatedValue(),
				parsedValue: this._getAggregatedParsedValue(),
				state: {
					valid,
					invalid: !valid,
					pristine: this._isPristine(),
					dirty: this._isDirty(),
					touched: this._isTouched(),
					focused: this._isFocused()
				}
			},
			bubbles: true,
			composed: true
		}));
	}
	/**
	* Registers fieldset at parent container.
	* @private
	*/
	_registerAtContainer() {
		const valid = this._getOverallValidity();
		this.dispatchEvent(new CustomEvent("pd-form-element-register", {
			detail: {
				name: this.name || this.id || "fieldset",
				value: this._getAggregatedValue(),
				parsedValue: this._getAggregatedParsedValue(),
				state: {
					pristine: this._isPristine(),
					dirty: this._isDirty(),
					touched: this._isTouched(),
					focused: this._isFocused(),
					valid,
					invalid: !valid
				}
			},
			bubbles: true,
			composed: true
		}));
	}
	/**
	* Unregisters fieldset from parent container.
	* @private
	*/
	_unRegisterAtContainer() {
		document.dispatchEvent(new CustomEvent("pd-form-element-unregister", {
			detail: {
				name: this.name || this.id || "fieldset",
				value: this._getAggregatedValue()
			},
			bubbles: true,
			composed: true
		}));
	}
	/** Runs all field validators and returns overall validity. */
	async runValidators() {
		const validations = [];
		this._fields.forEach((field) => {
			const result = field.element?.runValidators();
			if (result instanceof Promise) validations.push(result);
		});
		const results = await Promise.all(validations);
		let index = 0;
		this._fields.forEach((field) => {
			const isValid = results[index];
			field.valid = isValid;
			field.invalid = !isValid;
			index++;
		});
		return this._getOverallValidity();
	}
	/** Resets all child fields to their initial values. */
	reset() {
		this._fields.forEach((field) => {
			const element = field.element;
			if (typeof element?.reset === "function") element.reset();
		});
	}
	/** Clears all child fields. */
	clear() {
		this._fields.forEach((field) => {
			const element = field.element;
			if (typeof element?.clear === "function") element.clear();
		});
	}
	/** Returns the current status map of all registered child fields. */
	getStatus() {
		return this._fields;
	}
	/** Whether all child fields are currently valid. */
	get valid() {
		return this._getOverallValidity();
	}
	/** Current aggregated value object from all child fields. */
	get value() {
		return this._getAggregatedValue();
	}
};
__decorate([property({ type: String })], PdFormFieldset.prototype, "name", void 0);
__decorate([property({ type: String })], PdFormFieldset.prototype, "legend", void 0);
__decorate([property({
	type: String,
	reflect: true
})], PdFormFieldset.prototype, "span", void 0);
__decorate([state()], PdFormFieldset.prototype, "_fields", void 0);
//#endregion
export { PdFormFieldset };
